import { useState } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Users,
  Gavel,
  GraduationCap,
  Stethoscope,
  Clock,
  MapPin,
  Phone,
  Check,
  Heart,
  AlertTriangle,
  FileText,
  ClipboardCheck,
  Handshake,
  ScrollText,
  Quote,
  Award,
  Target,
} from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { PRIMARY_CTAS } from "@/content/primary-cta";
import { trackPlacementCTA } from "@/lib/cta-placement";
import { ConversionBand } from "@/components/site/ConversionBand";
import { FAQSection } from "@/components/site/FAQSection";
import { buildFAQPageJsonLd, type FAQItem } from "@/content/faq";
import { CalendlyInlineWidget } from "@/components/site/CalendlyInlineWidget";
import { FSAPConsultForm } from "@/components/site/FSAPConsultForm";
import { TierLeadCaptureForm } from "@/components/site/TierLeadCaptureForm";
import { StrategySessionModal } from "@/components/advocacy/StrategySessionModal";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";
import { trackHeroCTAClick } from "@/lib/hero-analytics";
import { setSelectedBookTier } from "@/lib/book-click-tracking";
import { buildInquireHref } from "@/lib/inquire-link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";


const FSAP_FAQ: FAQItem[] = [
  {
    q: "How do the retainer tiers differ in practice?",
    a: "Foundation is monthly guidance — strategy sessions, document review, email support, and limited school communication. Priority adds active representation, meeting attendance, faster response times, and priority scheduling. Elite adds after-hours access, urgent response support, expanded accompaniment, and multi-system coordination. All tiers are custom-quoted based on scope, urgency, and setting count.",
  },
  {
    q: "Can I move between retainer tiers during the engagement?",
    a: "Yes. Tiers are re-evaluated at each renewal cycle, and mid-cycle upgrades are allowed when a case escalates (new ARD, court date, hospitalization, or crisis window). Downgrades take effect at the next renewal so scope stays stable during active advocacy.",
  },
  {
    q: "How fast will I get a response, and how do after-hours and urgent support work?",
    a: "Foundation clients receive responses within two business days on email during standard hours. Priority clients receive same-business-day responses and expedited scheduling. Elite clients receive after-hours access for urgent, case-relevant events (imminent ARD, court appearance, medical escalation). After-hours is not a 24/7 crisis line — genuine emergencies still require 911, Texas 988, or the appropriate crisis service.",
  },
  {
    q: "Do you travel outside San Antonio, and how are travel costs handled?",
    a: "Yes. FSAP is based in San Antonio, Texas, with travel availability across Texas and beyond by arrangement. Travel time, mileage, lodging, and per-diem are quoted in writing before the trip and confirmed in the engagement addendum — no surprise line items.",
  },
  {
    q: "What happens after the discovery consultation?",
    a: "Within a few business days you receive a written recommendation: which service structure fits (retainer tier or à la carte), scope of representation, communication standards, boundaries, and fees. If it's a match, we move to an engagement agreement, activate the advocacy plan, prep documents and meetings, and set the accompaniment schedule. If FSAP isn't the right fit, you receive referrals where appropriate.",
  },
  {
    q: "Is FSAP only for churches or faith-based families?",
    a: "No. FSAP is faith-integrated and trauma-informed, but it is open to any family, student, or adult who needs advocacy — regardless of faith background. Faith is honored when it matters to you and never imposed when it doesn't.",
  },
  {
    q: "Is FSAP only for AI or technology experts?",
    a: "No. FSAP is a human advocacy service. You do not need technical, legal, or educational expertise to work with FSAP — that is exactly what the partnership provides. We translate the systems for you.",
  },
  {
    q: "Do I need a large following, platform, or public profile to qualify?",
    a: "No. FSAP is not tied to visibility, audience size, or notoriety. It is capacity-limited by care, not by clout. Every applicant is evaluated on fit, urgency, and scope — not platform.",
  },
  {
    q: "Are outcomes guaranteed?",
    a: "No. FSAP does not and cannot guarantee school, court, agency, medical, or institutional outcomes. What is guaranteed is preparation, strategy, documented advocacy, and experienced presence in the room. Decisions ultimately rest with institutions and systems outside of any advocate's control.",
  },
  {
    q: "Can schools, districts, or nonprofits engage FSAP?",
    a: "Yes. Schools, districts, nonprofits, agencies, and referring organizations can engage FSAP for consultation, staff training, compliance review, case-specific representation, or referred family support. Organizational engagements are scoped through the same intake funnel and formalized in writing.",
  },
];

/**
 * Canonical credential set — must stay in sync with the site-wide credential
 * audit (see scripts/validate-credentials.ts). Rendered as pill blocks in
 * the trust section to mirror the MakeChange chip pattern used elsewhere.
 */
const FSAP_CREDENTIALS = [
  { label: "PsyD", detail: "Doctor of Psychology" },
  { label: "ThD", detail: "Doctor of Theology" },
  { label: "DMin", detail: "Doctor of Ministry" },
  { label: "CERC", detail: "Certified Executive Restoration Coach" },
];

/**
 * Outcome-focused, anonymized composite scenarios drawn from 12+ years of
 * one-on-one advocacy practice. These are illustrative — not client
 * testimonials — and are labeled as such in the UI so no reader mistakes
 * them for a case guarantee or an endorsement.
 * TODO(claim): replace or supplement with signed, release-approved client
 * quotes as they are collected. Track in docs/CLAIMS_REGISTER.md.
 */
const OUTCOME_STORIES = [
  {
    id: "ard-reeval",
    setting: "ARD Meeting · Public School District",
    challenge:
      "A parent walked into a re-evaluation ARD after two failed meetings with no advocate in the room.",
    action:
      "FSAP prepared the parent, reviewed the evaluation packet, attended the meeting, and requested specific accommodations tied to the child's documented needs.",
    outcome:
      "A revised IEP with measurable accommodations and a written follow-up plan — reached inside a single meeting cycle.",
    timeline: "3 weeks · intake → revised IEP",
    context:
      "The family had attended two prior ARDs alone. Evaluations were dense, accommodations were vague, and prior notes did not reflect what was said in the room. The parent needed both preparation and a second set of eyes at the table.",
    process: [
      "Intake call and document intake — full evaluation packet, prior IEPs, teacher notes, private evaluations.",
      "Written pre-meeting brief for the parent: what to ask, what to request in writing, what to decline.",
      "In-room attendance at the ARD as the parent's advocate, tracking every accommodation offered vs. requested.",
      "Post-meeting written summary sent to the family within 24 hours, plus a follow-up email to the district.",
    ],
    results: [
      "Revised IEP with 6 measurable accommodations tied to documented needs.",
      "Written follow-up plan naming a district point of contact.",
      "Parent kept a clean paper trail for the next annual review.",
    ],
  },
  {
    id: "college-disability",
    setting: "College Disability Services · 4-Year University",
    challenge:
      "A student with complex needs was being pushed toward withdrawal after a semester of missed accommodations.",
    action:
      "FSAP coordinated a documentation review, a meeting with disability services, and an accompaniment plan for the following term's key appointments.",
    outcome:
      "The student re-enrolled with a written accommodation plan and a named point of contact inside the disability services office.",
    timeline: "6 weeks · across a term break",
    context:
      "The student had approved accommodations on paper but no working system to enforce them mid-semester. Faculty communication had broken down, and a withdrawal conversation was already on the table.",
    process: [
      "Documentation review — accommodation letter, syllabi, faculty email thread, and prior disability services notes.",
      "Strategy session with student and family on scope, boundaries, and what to ask for in writing.",
      "Joint meeting with disability services to reset the accommodation plan and assign a named contact.",
      "Accompaniment plan for the following term's highest-stakes appointments and check-ins.",
    ],
    results: [
      "Re-enrollment approved with a revised, written accommodation plan.",
      "Named point of contact inside disability services for the full next term.",
      "A repeatable weekly check-in rhythm the student now runs independently.",
    ],
  },
  {
    id: "multi-system",
    setting: "Multi-System Coordination · San Antonio",
    challenge:
      "A family was juggling school, medical, and agency appointments with no single person tracking the through-line.",
    action:
      "FSAP took the coordination seat — document intake, appointment prep, on-site presence at high-stakes meetings, and after-hours check-ins during peak weeks.",
    outcome:
      "One coordinated advocacy plan across three systems, with written summaries after every meeting the family kept as their own record.",
    timeline: "90 days · sustained retainer",
    context:
      "Three systems were producing three different plans for the same person. Appointments were being missed, decisions were being made without shared context, and the family was carrying the coordination load alone.",
    process: [
      "Master document intake across school, medical, and agency files.",
      "One shared coordination plan mapping every appointment, decision-maker, and deadline.",
      "On-site presence at the highest-stakes meetings in each system.",
      "After-hours check-ins during peak weeks and written summaries the family kept as their own record.",
    ],
    results: [
      "A single coordinated plan replacing three disconnected ones.",
      "Zero missed high-stakes appointments across the 90-day window.",
      "Family-owned paper trail transferable to any future advocate or attorney.",
    ],
  },
];


/**
 * Client voice slots. Left empty by design until signed releases are on file.
 * When a testimonial is added, include the source, setting, and date, and
 * log the claim in docs/CLAIMS_REGISTER.md.
 * TODO(claim): populate with release-approved testimonials.
 */
const CLIENT_VOICES: Array<{
  quote: string;
  attribution: string;
  setting: string;
}> = [];

/**
 * Full-Scope Advocacy Partner (FSAP) — premium advocacy service page.
 * Faith-integrated, trauma-informed, San Antonio + travel.
 * TODO(verify): confirm degree wording and any licensure claims before removing
 * the "Bachelor's in Interdisciplinary Education (Texas)" language.
 * TODO(legal): confirm final boundary + disclosure language with counsel before
 * publishing the "Legal & Ethical Boundaries" section publicly.
 */

const WHO_ITS_FOR = [
  "Parents preparing for ARD or IEP meetings",
  "Families needing advocacy in schools, colleges, universities, or community systems",
  "Students and adults with disabilities who need support, accompaniment, or representation",
  "Families under stress who need an experienced professional in the room",
];

const RETAINER_TIERS = [
  {
    name: "Foundation FSAP",
    price: "Custom",
    cadence: "monthly retainer",
    tagline: "Families needing monthly guidance and limited advocacy support.",
    features: [
      "Monthly strategy session",
      "Document review",
      "Email support",
      "Limited school communication support",
    ],
  },
  {
    name: "Priority FSAP",
    price: "Custom",
    cadence: "monthly retainer",
    tagline: "Families needing active representation and meeting support.",
    features: [
      "Everything in Foundation",
      "Meeting attendance",
      "Advocacy planning",
      "Faster response times",
      "Priority scheduling",
    ],
    featured: true,
    badge: "Most Requested",
  },
  {
    name: "Elite FSAP",
    price: "Custom",
    cadence: "monthly retainer",
    tagline: "Families needing high-touch support across multiple environments.",
    features: [
      "Everything in Priority",
      "After-hours access",
      "Urgent response support",
      "Expanded accompaniment",
      "Multi-system coordination",
    ],
  },
];

type TierCellValue = string | boolean;

const FSAP_COMPARISON_TIERS: {
  name: string;
  headline: string;
  featured?: boolean;
}[] = [
  { name: "Foundation", headline: "Monthly guidance" },
  { name: "Priority", headline: "Active representation", featured: true },
  { name: "Elite", headline: "High-touch, multi-system" },
];

const FSAP_COMPARISON_ROWS: {
  label: string;
  hint?: string;
  values: [TierCellValue, TierCellValue, TierCellValue];
}[] = [
  {
    label: "Strategy sessions",
    hint: "Structured planning calls with Dr. Sully.",
    values: ["1 / month", "2 / month", "Weekly cadence"],
  },
  {
    label: "Document & IEP/504 review",
    values: ["Monthly review window", "Rolling review", "Rolling + rapid-turn review"],
  },
  {
    label: "Email support",
    values: ["Business hours", "Priority queue", "Priority + escalation lane"],
  },
  {
    label: "Standard response time",
    hint: "Non-urgent written responses during business days.",
    values: ["2 business days", "1 business day", "Same business day"],
  },
  {
    label: "Meeting / ARD / IEP attendance",
    values: [false, "Included (scoped)", "Included (expanded)"],
  },
  {
    label: "School / district communication",
    values: ["Limited support", "Active representation", "Full representation"],
  },
  {
    label: "After-hours access",
    hint: "Evening and weekend contact windows for time-sensitive situations.",
    values: [false, false, true],
  },
  {
    label: "Urgent-response support",
    hint: "Rapid coordination when a situation escalates.",
    values: [false, "Business-hours triage", "24-hour urgent window"],
  },
  {
    label: "Multi-system coordination",
    hint: "Schools, clinicians, legal, and family stakeholders aligned.",
    values: [false, "Two systems", "Unlimited coordination"],
  },
  {
    label: "Travel & on-site presence",
    values: ["Local (San Antonio)", "Regional (travel billed)", "National (travel billed)"],
  },
  {
    label: "Priority scheduling",
    values: [false, true, true],
  },
  {
    label: "Best for",
    values: [
      "Families needing steady guidance",
      "Active advocacy across one setting",
      "Complex, multi-setting cases",
    ],
  },
];

const A_LA_CARTE = [
  {
    icon: GraduationCap,
    title: "ARD Strategy & Advocacy Specialist",
    scope: "Pre-meeting strategy, document review, parent coaching, and post-meeting debrief.",
  },
  {
    icon: Gavel,
    title: "IEP / ARD Representation Specialist",
    scope: "Attendance at ARD or IEP meetings, live advocacy, accommodation support, and meeting intervention.",
  },
  {
    icon: ShieldCheck,
    title: "Educational Advocacy & Compliance Specialist",
    scope: "IEP/504/evaluation review, compliance concerns, written recommendations, and district communication support.",
  },
  {
    icon: Users,
    title: "Advocacy Presence & Support",
    scope: "Companion support, ride-along support, and on-site presence for school, court, college, medical, or agency settings.",
  },
  {
    icon: AlertTriangle,
    title: "Emergency / After-Hours Support",
    scope: "Urgent check-ins, crisis-adjacent support, and event-based reinforcement.",
  },
];

const PRESENCE_SERVICES = [
  {
    icon: GraduationCap,
    title: "School & College Accompaniment",
    desc: "On-site presence for enrollment, disciplinary, or transition meetings.",
  },
  {
    icon: Gavel,
    title: "Court & Legal Appearances",
    desc: "Companion advocacy in courtrooms, mediations, and hearings.",
  },
  {
    icon: Stethoscope,
    title: "Medical & Clinical Appointments",
    desc: "Support in diagnosis conversations, treatment planning, and care coordination.",
  },
  {
    icon: Users,
    title: "Ride-Along & Transition Support",
    desc: "Safety, stability, and continuity for students and adults navigating change.",
  },
];

const INTAKE_FUNNEL = [
  {
    icon: FileText,
    title: "Inquiry",
    desc: "Parent, student, or referring partner submits a service request.",
  },
  {
    icon: Phone,
    title: "Screening Call",
    desc: "Short call to identify urgency, fit, setting, and support level needed.",
  },
  {
    icon: ClipboardCheck,
    title: "Discovery Consultation",
    desc: "Paid consultation to review documents, define needs, assess risk, and recommend service structure.",
  },
  {
    icon: Sparkles,
    title: "Service Match",
    desc: "Client is placed into an FSAP retainer or à la carte support.",
  },
  {
    icon: Handshake,
    title: "Agreement + Boundaries",
    desc: "Scope, fees, communication standards, emergency limitations, and consent are reviewed in writing.",
  },
  {
    icon: ShieldCheck,
    title: "Service Activation",
    desc: "Advocacy plan, meeting prep, document strategy, and accompaniment schedule begin.",
  },
];

const BOUNDARIES = [
  "No legal advice unless separately licensed to provide it",
  "No guarantee of school, court, agency, or institutional outcomes",
  "No emergency medical intervention or crisis stabilization beyond agreed support role",
  "Transportation support governed by written consent, safety policies, liability terms, and service limitations",
  "Mandated reporting duties stated clearly if abuse, neglect, danger, or legal risk is disclosed",
  "Confidentiality, records handling, communication boundaries, and release-of-information procedures are documented",
  "Scope of representation is defined in writing for each case, meeting, or event",
];

const AUTHORITY = [
  "12+ years of direct one-on-one advocacy and support experience",
  "Extensive work supporting a 39-year-old quadriplegic woman with cerebral palsy across life systems",
  "Educational, human service, and daily living navigation across a 15-year college journey",
  "Deep exposure to real-world disability advocacy, systems friction, and long-term support coordination",
  "Texas-based with San Antonio office and travel availability",
];

const DIFFERENTIATORS = [
  {
    icon: Heart,
    title: "I don't just advise — I show up.",
    desc: "Physical presence in the rooms where decisions actually get made.",
  },
  {
    icon: ShieldCheck,
    title: "Faith-integrated · Trauma-informed",
    desc: "Anchored in dignity, discernment, and clinical-grade care standards.",
  },
  {
    icon: Users,
    title: "Systems-level expertise",
    desc: "Fluent across schools, agencies, courts, hospitals, and social services.",
  },
  {
    icon: MapPin,
    title: "San Antonio + travel",
    desc: "Local presence in San Antonio, Texas with travel availability nationwide.",
  },
];

const Advocacy = () => {
  const [openStoryId, setOpenStoryId] = useState<string | null>(null);
  const [strategyTier, setStrategyTier] = useState<string | null>(null);
  const openStory = OUTCOME_STORIES.find((s) => s.id === openStoryId) ?? null;
  return (
  <div className="min-h-screen bg-background text-foreground pb-[88px] md:pb-0">

    <PageMeta
      title="Full-Scope Advocacy Partner (FSAP) — Dr. Sully Speaks"
      description="Premium advocacy, strategic representation, and presence-based support for families and students navigating special education, transition systems, and high-stakes environments. San Antonio + travel."
      path="/advocacy"
      jsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          buildFAQPageJsonLd(FSAP_FAQ, "https://drsullyspeaks.com/advocacy#faq"),
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://drsullyspeaks.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Advocacy",
                item: "https://drsullyspeaks.com/advocacy",
              },
            ],
          },
        ],
      }}
    />

    <SiteNav />

    {/* FSAP sub-nav — links to pricing, process, intake, agreement */}
    <div className="border-b border-gold/20 bg-navy-elevated/60 backdrop-blur">
      <div className="container max-w-6xl px-6 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em] font-display">
        <span className="text-gold">FSAP</span>
        <Link to="/advocacy/pricing" onClick={() => trackCTA("advocacy_subnav_click", { to: "pricing" })} className="text-silver hover:text-white">Pricing</Link>
        <Link to="/advocacy/how-it-works" onClick={() => trackCTA("advocacy_subnav_click", { to: "how" })} className="text-silver hover:text-white">How it works</Link>
        <Link to="/advocacy/intake" onClick={() => trackCTA("advocacy_subnav_click", { to: "intake" })} className="text-silver hover:text-white">Intake form</Link>
        <Link to="/advocacy/agreement" onClick={() => trackCTA("advocacy_subnav_click", { to: "agreement" })} className="text-silver hover:text-white">Agreement draft</Link>
        <Link to="/advocacy/pricing#compassion" onClick={() => trackCTA("advocacy_subnav_click", { to: "compassion" })} className="text-gold hover:text-white">Compassion Plan · $99/mo</Link>
      </div>
    </div>

    {/* HERO */}
    <section data-hero-section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-hero overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--gold)/0.1),transparent_55%),radial-gradient(circle_at_80%_80%,hsl(var(--orange)/0.08),transparent_55%)]"
      />
      <div className="container relative max-w-4xl text-center">
        <Reveal variant="up">
          <span className="eyebrow !mx-auto inline-flex">
            <Sparkles className="h-3 w-3" /> Full-Scope Advocacy Partner · FSAP
          </span>
          <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05]">
            Strategy. Representation.{" "}
            <span className="text-gradient-gold">Presence.</span>
          </h1>
          <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Premium advocacy and presence-based support for families and
            students navigating special education, transition systems, and
            high-stakes environments. Based in San Antonio, Texas — with travel
            availability.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#book"
              data-book-location="hero"
              onClick={() =>
                trackPlacementCTA("hero", PRIMARY_CTAS.advocacy, { surface: "advocacy_hero" }) &&
                trackHeroCTAClick("advocacy_hero", "advocacy_book_discovery_click", {
                  location: "hero",
                  destination: "#book",
                })
              }
              className="group inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-5 shadow-cta hover:brightness-110 transition-all"
            >
              {PRIMARY_CTAS.advocacy.label}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#retainer"
              onClick={() =>
                trackHeroCTAClick("advocacy_hero", "advocacy_see_plans_click", {
                  location: "hero",
                  destination: "#retainer",
                })
              }
              className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-8 py-5 hover:bg-gold/10 hover:border-gold transition-all"
            >
              See FSAP Plans
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {[
              "By application · Limited capacity",
              "San Antonio + Travel",
              "Faith-integrated · Trauma-informed",
            ].map((label) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 border border-gold/30 bg-navy-elevated/50 px-3 py-1.5"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-gold" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-silver">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* WHAT FSAP PROVIDES */}
    <section className="py-20 md:py-28 border-t border-border">
      <div className="container max-w-4xl">
        <Reveal variant="up">
          <span className="eyebrow">What FSAP Provides</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            More than advice —{" "}
            <span className="text-gradient-gold">real presence, real representation.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            FSAP is a premium, relationship-based service for families,
            students, and adults with special needs who need strategy,
            representation, physical presence, and ongoing support across
            school, college, court, appointments, and complex system
            navigation.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {[
            "ARD Strategy & Advocacy Specialist",
            "IEP / ARD Representation Specialist",
            "Educational Advocacy & Compliance Specialist",
            "Companion and accompaniment support",
            "Event, school, college, court, and systems-based advocacy presence",
            "After-hours check-ins and urgent support options",
          ].map((item, i) => (
            <Reveal key={item} variant="up" delay={i * 40}>
              <div className="flex items-start gap-3 border border-border bg-navy/60 p-5 hover:border-gold/40 transition-colors">
                <Check className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <span className="text-white/90 text-sm md:text-base leading-relaxed">
                  {item}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHO IT'S FOR */}
    <section className="py-20 md:py-28 border-t border-border bg-navy-deep/40">
      <div className="container max-w-5xl">
        <Reveal variant="up" className="max-w-3xl mb-12">
          <span className="eyebrow">Who This Is For</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Built for families who need{" "}
            <span className="text-gradient-gold">someone in the room.</span>
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-4">
          {WHO_ITS_FOR.map((item, i) => (
            <Reveal key={item} variant="up" delay={i * 60}>
              <div className="flex items-start gap-3 border-l-2 border-gold/50 bg-navy/60 pl-5 pr-5 py-5">
                <span className="text-white/90 text-sm md:text-base leading-relaxed">
                  {item}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* RETAINER TIERS */}
    <section id="retainer" className="py-20 md:py-28 border-t border-border">
      <div className="container">
        <Reveal variant="up" className="max-w-3xl mb-14">
          <span className="eyebrow">FSAP Retainer Plans</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Three tiers. <span className="text-gradient-gold">Custom retainers.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Retainers keep priority access, response time, and continuity
            intact. Every engagement begins with a screening call and paid
            discovery consultation to confirm fit before any agreement is
            signed.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {RETAINER_TIERS.map((tier, i) => (
            <Reveal key={tier.name} variant="up" delay={i * 80}>
              <div
                className={`relative h-full flex flex-col border bg-navy/80 backdrop-blur-sm p-6 md:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_hsl(var(--gold)/0.35)] ${
                  tier.featured
                    ? "border-gold/60 bg-gradient-to-br from-gold/[0.08] via-navy to-navy shadow-[0_20px_60px_-25px_hsl(var(--gold)/0.4)]"
                    : "border-border hover:border-gold/40"
                }`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-orange text-navy-deep font-display uppercase tracking-[0.2em] text-[10px] px-3 py-1.5 shadow-cta">
                    <Sparkles className="h-3 w-3" /> {tier.badge}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {tier.name}
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display text-white text-4xl md:text-5xl">
                    {tier.price}
                  </span>
                  <span className="text-silver text-xs uppercase tracking-[0.2em]">
                    {tier.cadence}
                  </span>
                </div>
                <p className="text-silver text-sm mt-3 leading-relaxed">
                  {tier.tagline}
                </p>
                <ul className="mt-6 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                      <span className="text-white/85 text-sm leading-relaxed">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    trackCTA("advocacy_tier_apply_click", {
                      tier: tier.name,
                      location: "retainer_cards",
                      destination: "strategy_session_modal",
                    });
                    setSelectedBookTier(tier.name, "retainer_cards");
                    setStrategyTier(tier.name);
                  }}
                  className={`mt-7 inline-flex items-center justify-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-6 py-4 transition-all ${
                    tier.featured
                      ? "bg-cta text-white shadow-cta hover:brightness-110"
                      : "border border-gold/50 text-white hover:bg-gold/10 hover:border-gold"
                  }`}
                >
                  Apply
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="text-silver/70 text-xs text-center mt-8 max-w-2xl mx-auto">
          Retainer pricing is custom and set during discovery based on case
          complexity, geography, response expectations, and support scope.
        </p>
      </div>
    </section>

    {/* TIER COMPARISON MATRIX */}
    <section id="compare" className="py-20 md:py-28 border-t border-border bg-navy-deep/30">
      <div className="container">
        <Reveal variant="up" className="max-w-3xl mb-12">
          <span className="eyebrow">Side-by-Side Comparison</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            What's included.{" "}
            <span className="text-gradient-gold">How fast we move.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Every FSAP retainer is custom-quoted. Use this matrix to identify
            the tier that matches your case complexity, response expectations,
            and after-hours coverage needs before the discovery call.
          </p>
        </Reveal>

        {/* QUICK PICK — scannable "choose this if…" guidance */}
        <Reveal variant="up" className="grid md:grid-cols-3 gap-4 md:gap-5 mb-10 md:mb-14">
          {[
            {
              name: "Foundation",
              headline: "Monthly guidance",
              response: "2-day response",
              coverage: "Business hours",
              chooseIf: [
                "You need a professional in your corner monthly",
                "Meetings are stable; you mostly need prep and review",
                "One school or setting to coordinate",
              ],
            },
            {
              name: "Priority",
              headline: "Active representation",
              response: "Same/next-day response",
              coverage: "Priority queue",
              featured: true,
              chooseIf: [
                "You have an active ARD, IEP, or 504 in motion",
                "You want Dr. Sully in the room, not just on email",
                "Two systems to coordinate (school + clinical, etc.)",
              ],
            },
            {
              name: "Elite",
              headline: "High-touch, multi-system",
              response: "Same-day + after-hours",
              coverage: "Urgent-response window",
              chooseIf: [
                "Court, hospitalization, or crisis windows are live",
                "You need after-hours access to a decision-maker",
                "Three or more systems must stay aligned",
              ],
            },
          ].map((t) => (
            <div
              key={t.name}
              className={`relative flex flex-col border ${
                t.featured
                  ? "border-gold bg-gold/[0.06]"
                  : "border-border bg-navy/60"
              } p-6 md:p-7`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-orange text-navy-deep font-display uppercase tracking-[0.2em] text-[9px] px-2 py-1">
                  <Sparkles className="h-2.5 w-2.5" /> Most Requested
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {t.name}
              </div>
              <div className="font-display text-white text-xl md:text-2xl mt-2 leading-tight">
                {t.headline}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="inline-flex items-center gap-1.5 border border-border bg-navy-deep/60 text-silver text-[11px] uppercase tracking-[0.2em] px-2.5 py-1">
                  <Clock className="h-3 w-3 text-gold" /> {t.response}
                </span>
                <span className="inline-flex items-center gap-1.5 border border-border bg-navy-deep/60 text-silver text-[11px] uppercase tracking-[0.2em] px-2.5 py-1">
                  <ShieldCheck className="h-3 w-3 text-gold" /> {t.coverage}
                </span>
              </div>
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-[10px] uppercase tracking-[0.3em] text-silver mb-3">
                  Choose this if
                </div>
                <ul className="space-y-2.5">
                  {t.chooseIf.map((line) => (
                    <li key={line} className="flex gap-2.5 text-white text-sm leading-snug">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to={buildInquireHref({
                  lane: "advocacy",
                  intent: "tier-recommendation",
                  tier: t.name,
                  source: "quickpick",
                })}
                onClick={() =>
                  trackCTA("advocacy_tier_quickpick_click", {
                    tier: t.name,
                    location: "quickpick",
                    destination: buildInquireHref({
                      lane: "advocacy",
                      intent: "tier-recommendation",
                      tier: t.name,
                      source: "quickpick",
                    }),
                  })
                }
                className={`mt-6 inline-flex items-center justify-between gap-2 px-4 py-3 font-display uppercase tracking-[0.2em] text-[11px] transition-colors ${
                  t.featured
                    ? "bg-gold text-navy-deep hover:bg-gold/90"
                    : "border border-border text-white hover:border-gold hover:text-gold"
                }`}
              >
                Start with {t.name}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </Reveal>

        {/* AT-A-GLANCE SUMMARY TABLE — What's included / Response access / Travel */}
        <Reveal variant="up" className="mb-10 md:mb-14 border border-gold/30 bg-navy/70">
          <div className="p-5 md:p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">At a Glance</div>
              <div className="font-display text-white text-lg md:text-xl mt-1">
                Retainer tiers summary
              </div>
            </div>
            <div className="text-silver/70 text-[11px] uppercase tracking-[0.2em]">
              Custom-quoted · Monthly retainer
            </div>
          </div>

          {/* Desktop / tablet: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border bg-navy-deep/60">
                  <th className="p-4 md:p-5 text-[10px] uppercase tracking-[0.3em] text-silver font-normal w-[22%]">
                    Dimension
                  </th>
                  {[
                    { name: "Foundation", featured: false },
                    { name: "Priority", featured: true },
                    { name: "Elite", featured: false },
                  ].map((t) => (
                    <th
                      key={t.name}
                      className={`p-4 md:p-5 ${t.featured ? "bg-gold/[0.06]" : ""}`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        {t.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    label: "What's included",
                    icon: ClipboardCheck,
                    values: [
                      "Monthly strategy call, document & IEP/504 review, email guidance, limited school communication.",
                      "Everything in Foundation + ARD/IEP meeting attendance, active school representation, priority scheduling, multi-system coordination (2 systems).",
                      "Everything in Priority + expanded accompaniment, urgent-response support, unlimited multi-system coordination, dedicated case management.",
                    ],
                  },
                  {
                    label: "Response access",
                    icon: Clock,
                    values: [
                      "Email during business hours · 2 business-day response · no after-hours access.",
                      "Priority queue · same or next business day · escalation lane for active meetings · no after-hours access.",
                      "Same-business-day + after-hours access for case-relevant urgent events (imminent ARD, court, medical escalation). Not a 24/7 crisis line — emergencies still require 911 or 988.",
                    ],
                  },
                  {
                    label: "Travel cost handling",
                    icon: MapPin,
                    values: [
                      "Local to San Antonio metro. On-site mileage inside 30 miles included. Beyond 30 miles billed at IRS standard mileage.",
                      "Regional (Texas + adjoining states). Travel billed at cost: mileage, airfare, ground transport, lodging, and a half-day on-site rate. Estimate provided in writing before booking.",
                      "National + expedited travel. Travel billed at cost with a full-day on-site rate. Same-week and after-hours travel available; expedited booking fees pre-approved in writing.",
                    ],
                  },
                ].map((row) => (
                  <tr key={row.label} className="border-b border-border/60 last:border-0 align-top">
                    <td className="p-4 md:p-5">
                      <div className="flex items-start gap-2.5">
                        <row.icon className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                        <div className="text-white text-sm font-medium leading-snug">
                          {row.label}
                        </div>
                      </div>
                    </td>
                    {row.values.map((v, i) => (
                      <td
                        key={i}
                        className={`p-4 md:p-5 text-sm leading-relaxed ${
                          i === 1 ? "bg-gold/[0.04] text-white" : "text-white/85"
                        }`}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked */}
          <div className="sm:hidden divide-y divide-border/60">
            {[
              { name: "Foundation", featured: false, included: "Monthly strategy, document/IEP review, email guidance, limited school communication.", response: "Business hours · 2-day response · no after-hours.", travel: "Local (San Antonio). 30 mi included, then IRS mileage." },
              { name: "Priority", featured: true, included: "Foundation + ARD/IEP attendance, active representation, priority scheduling, 2-system coordination.", response: "Priority queue · same/next-day · escalation lane.", travel: "Regional. Travel billed at cost + half-day on-site rate. Estimate in writing." },
              { name: "Elite", featured: false, included: "Priority + expanded accompaniment, urgent response, unlimited systems, dedicated case mgmt.", response: "Same-day + after-hours for urgent case events. Not a 24/7 crisis line.", travel: "National + expedited. Travel at cost + full-day rate. Same-week booking available." },
            ].map((t) => (
              <div key={t.name} className={`p-5 ${t.featured ? "bg-gold/[0.05]" : ""}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{t.name}</div>
                <dl className="mt-3 space-y-3">
                  <div>
                    <dt className="flex items-center gap-1.5 text-silver text-[11px] uppercase tracking-[0.2em]">
                      <ClipboardCheck className="h-3 w-3 text-gold" /> What's included
                    </dt>
                    <dd className="text-white text-sm mt-1 leading-relaxed">{t.included}</dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-silver text-[11px] uppercase tracking-[0.2em]">
                      <Clock className="h-3 w-3 text-gold" /> Response access
                    </dt>
                    <dd className="text-white text-sm mt-1 leading-relaxed">{t.response}</dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-silver text-[11px] uppercase tracking-[0.2em]">
                      <MapPin className="h-3 w-3 text-gold" /> Travel cost handling
                    </dt>
                    <dd className="text-white text-sm mt-1 leading-relaxed">{t.travel}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>

          <div className="p-4 md:p-5 border-t border-border bg-navy-deep/40 text-silver/80 text-xs leading-relaxed">
            <span className="text-gold font-medium uppercase tracking-[0.2em] text-[10px] mr-2">Travel policy:</span>
            All travel is billed at documented cost with receipts. Estimates are
            provided and approved in writing before any trip is booked. Ground
            travel under 30 miles is included on Foundation; beyond that, mileage
            follows the current IRS standard rate. Priority and Elite include a
            per-diem on-site rate quoted during discovery.
          </div>
        </Reveal>

        {/* Desktop matrix */}
        <Reveal variant="up" className="hidden md:block overflow-x-auto border border-border bg-navy/60">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-navy-deep/60">
                <th className="p-5 text-[10px] uppercase tracking-[0.3em] text-silver font-normal w-[28%]">
                  Coverage Area
                </th>
                {FSAP_COMPARISON_TIERS.map((t) => (
                  <th
                    key={t.name}
                    className={`p-5 align-bottom ${t.featured ? "bg-gold/[0.06]" : ""}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                      {t.name}
                    </div>
                    <div className="font-display text-white text-xl md:text-2xl mt-2 leading-tight">
                      {t.headline}
                    </div>
                    {t.featured && (
                      <div className="inline-flex items-center gap-1.5 mt-2 bg-orange text-navy-deep font-display uppercase tracking-[0.2em] text-[9px] px-2 py-1">
                        <Sparkles className="h-2.5 w-2.5" /> Most Requested
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FSAP_COMPARISON_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-border/60 last:border-0">
                  <td className="p-5 align-top">
                    <div className="text-white text-sm font-medium">{row.label}</div>
                    {row.hint && (
                      <div className="text-silver/70 text-xs mt-1 leading-relaxed">
                        {row.hint}
                      </div>
                    )}
                  </td>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={`p-5 align-top text-sm ${
                        FSAP_COMPARISON_TIERS[i].featured
                          ? "bg-gold/[0.04] text-white"
                          : "text-white/85"
                      }`}
                    >
                      {v === true ? (
                        <Check className="h-4 w-4 text-gold" aria-label="Included" />
                      ) : v === false ? (
                        <span className="text-silver/40" aria-label="Not included">—</span>
                      ) : (
                        <span className="leading-relaxed">{v}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-6">
          {FSAP_COMPARISON_TIERS.map((tier, ti) => (
            <Reveal
              key={tier.name}
              variant="up"
              delay={ti * 60}
              className={`border p-6 ${
                tier.featured
                  ? "border-gold/60 bg-gradient-to-br from-gold/[0.08] via-navy to-navy"
                  : "border-border bg-navy/60"
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {tier.name}
              </div>
              <div className="font-display text-white text-xl mt-2">
                {tier.headline}
              </div>
              <dl className="mt-5 space-y-4">
                {FSAP_COMPARISON_ROWS.map((row) => (
                  <div key={row.label} className="border-t border-border/60 pt-3">
                    <dt className="text-silver text-[11px] uppercase tracking-[0.2em]">
                      {row.label}
                    </dt>
                    <dd className="text-white text-sm mt-1.5 leading-relaxed">
                      {row.values[ti] === true
                        ? "Included"
                        : row.values[ti] === false
                          ? "Not included"
                          : row.values[ti]}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          ))}
        </div>

        <TierLeadCaptureForm />

        {/* Best-fit lead capture CTA */}
        <Reveal
          variant="up"
          className="mt-14 border border-gold/40 bg-gradient-to-br from-gold/[0.09] via-navy to-navy p-8 md:p-12"
        >
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <span className="eyebrow text-gold">Not sure which tier fits?</span>
              <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl mt-4 leading-tight">
                Get a written tier recommendation after a paid discovery call.
              </h3>
              <p className="text-silver text-sm md:text-base mt-4 leading-relaxed max-w-xl">
                Share case complexity, setting count, and response expectations.
                Within a few business days you receive a written recommendation
                naming the best-fit tier (or à la carte scope), communication
                standards, and fee range before any retainer is signed.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-white/85 text-sm">
                {[
                  "Screening → paid discovery",
                  "Written tier recommendation",
                  "Fee range disclosed up front",
                  "No obligation to retain",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to={buildInquireHref({
                  lane: "advocacy",
                  intent: "tier-recommendation",
                  source: "best_fit_cta",
                })}
                onClick={() =>
                  trackCTA("advocacy_request_tier_recommendation_click", {
                    location: "best_fit_cta",
                    destination: buildInquireHref({
                      lane: "advocacy",
                      intent: "tier-recommendation",
                      source: "best_fit_cta",
                    }),
                  })
                }
                className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
              >
                Request tier recommendation
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <a
                href="#book"
                data-book-location="best_fit_cta"
                onClick={() =>
                  trackPlacementCTA("mid_page", PRIMARY_CTAS.advocacy, { surface: "advocacy_best_fit" }) &&
                  trackCTA("advocacy_book_discovery_click", {
                    location: "best_fit_cta",
                    destination: "#book",
                  })
                }
                className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-gold/10 hover:border-gold transition-all"
              >
                {PRIMARY_CTAS.advocacy.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <p className="text-silver/70 text-[11px] text-center leading-relaxed">
                Response within one business day. Discovery consultations
                are paid and confirm fit before any engagement.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* À LA CARTE */}
    <section className="py-20 md:py-28 border-t border-border bg-navy-deep/40">
      <div className="container">
        <Reveal variant="up" className="max-w-3xl mb-14">
          <span className="eyebrow">Per-Event / À La Carte Services</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Single meetings.{" "}
            <span className="text-gradient-gold">Focused support.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Per-event rates apply to single meetings, appearances, reviews,
            accompaniment requests, and urgent support windows — ideal before
            committing to a retainer or as overflow coverage for existing FSAP
            clients.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {A_LA_CARTE.map(({ icon: Icon, title, scope }, i) => (
            <Reveal key={title} variant="up" delay={i * 70}>
              <div className="h-full flex flex-col border border-border bg-navy/60 p-6 hover:border-gold/40 transition-colors">
                <Icon className="h-6 w-6 text-gold" />
                <h3 className="font-display uppercase text-white text-lg mt-4 leading-tight">
                  {title}
                </h3>
                <p className="text-silver text-sm mt-4 leading-relaxed">
                  {scope}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* PRESENCE */}
    <section className="py-20 md:py-28 border-t border-border">
      <div className="container">
        <Reveal variant="up" className="max-w-3xl mb-14">
          <span className="eyebrow">Advocacy Presence & Support</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            In-person support where{" "}
            <span className="text-gradient-gold">it matters most.</span>
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRESENCE_SERVICES.map(({ icon: Icon, title, desc }, i) => (
            <Reveal key={title} variant="up" delay={i * 60}>
              <div className="h-full border border-border bg-navy/60 p-6 hover:border-gold/40 transition-colors">
                <Icon className="h-6 w-6 text-gold" />
                <h3 className="font-display uppercase text-white text-base mt-4 leading-tight">
                  {title}
                </h3>
                <p className="text-silver text-sm mt-3 leading-relaxed">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* INTAKE FUNNEL */}
    <section className="py-20 md:py-28 border-t border-border bg-navy-deep/40">
      <div className="container max-w-6xl">
        <Reveal variant="up" className="max-w-3xl mb-14">
          <span className="eyebrow">Intake Funnel</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            How families{" "}
            <span className="text-gradient-gold">begin with FSAP.</span>
          </h2>
        </Reveal>

        <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {INTAKE_FUNNEL.map(({ icon: Icon, title, desc }, i) => (
            <Reveal key={title} variant="up" delay={i * 60}>
              <li className="h-full border border-border bg-navy/60 p-6 hover:border-gold/40 transition-colors relative">
                <div className="absolute top-4 right-4 font-display text-gold/40 text-2xl">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <Icon className="h-6 w-6 text-gold" />
                <h3 className="font-display uppercase text-white text-base mt-4 leading-tight">
                  {title}
                </h3>
                <p className="text-silver text-sm mt-3 leading-relaxed">{desc}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>

    {/* AUTHORITY */}
    <section className="py-20 md:py-28 border-t border-border">
      <div className="container max-w-5xl grid md:grid-cols-2 gap-12 items-start">
        <Reveal variant="up">
          <span className="eyebrow">Credibility</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Lived advocacy.{" "}
            <span className="text-gradient-gold">Not theory.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Over a decade of walking families through the systems that most
            professionals only read about.
          </p>
        </Reveal>

        <Reveal variant="up" delay={100}>
          <ul className="space-y-4">
            {AUTHORITY.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 border-l-2 border-gold/50 pl-4 py-1"
              >
                <span className="text-white/90 text-sm md:text-base leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Secondary CTAs — surface the best-fit tier recommendation path
            and #book together so credibility momentum has a direct exit. */}
        <Reveal variant="up" delay={150} className="md:col-span-2">
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3 items-start sm:items-center border-t border-border pt-6">
            <span className="text-silver/70 text-xs uppercase tracking-[0.2em] shrink-0">
              Next step
            </span>
            <div className="flex flex-wrap gap-3">
              <Link
                to={buildInquireHref({
                  lane: "advocacy",
                  intent: "tier-recommendation",
                  source: "credibility",
                })}
                onClick={() =>
                  trackCTA("advocacy_request_tier_recommendation_click", {
                    location: "credibility_secondary_cta",
                    destination: buildInquireHref({
                      lane: "advocacy",
                      intent: "tier-recommendation",
                      source: "credibility",
                    }),
                  })
                }
                className="inline-flex items-center gap-2 border border-gold/60 text-gold hover:bg-gold/10 px-5 py-2.5 text-sm font-display uppercase tracking-[0.15em] transition-colors"
              >
                Get best-fit tier
              </Link>
              <a
                href="#book"
                data-book-location="credibility_secondary_cta"
                onClick={() =>
                  trackCTA("advocacy_book_click", {
                    location: "credibility_secondary_cta",
                    destination: "#book",
                  })
                }
                className="inline-flex items-center gap-2 border border-border text-white hover:border-white/60 px-5 py-2.5 text-sm font-display uppercase tracking-[0.15em] transition-colors"
              >
                Jump to booking
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>


    {/*
      TRUST & OUTCOMES
      ----------------
      Credibility reinforcement layer sitting between "Credibility" and
      "Positioning". Three parts, each labeled honestly:
        1. Credentials — canonical set, rendered as MakeChange-style pills.
        2. Outcome stories — anonymized *composite* scenarios, clearly
           labeled as illustrative (not testimonials, not guarantees).
        3. Client voices — real testimonials only when a signed release
           exists. Falls back to a transparent placeholder otherwise so
           we never fabricate quotes.
    */}
    <section className="py-20 md:py-28 border-t border-border">
      <div className="container max-w-6xl">
        <Reveal variant="up" className="max-w-3xl mx-auto text-center mb-16">
          <span className="eyebrow !mx-auto inline-flex">Trust &amp; Outcomes</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Credentialed. Composite.{" "}
            <span className="text-gradient-gold">Consented.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Credentials on the wall, real-world scenarios in the room, and
            client voices only when we have written permission to share them.
          </p>
        </Reveal>

        {/* Credentials — canonical PsyD · ThD · DMin · CERC pills */}
        <Reveal variant="up">
          <div className="flex flex-wrap gap-3 justify-center mb-16">
            {FSAP_CREDENTIALS.map(({ label, detail }) => (
              <div
                key={label}
                className="group border border-gold/40 bg-navy-deep/60 px-5 py-3 hover:border-gold transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-gold" aria-hidden />
                  <span className="font-display uppercase tracking-[0.2em] text-gold text-sm">
                    {label}
                  </span>
                </div>
                <div className="text-silver/80 text-xs mt-1">{detail}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Authority & coaching background — reinforces the credential pills
            with the practitioner story, disciplines, and philosophy that
            sit behind the PsyD · ThD · DMin · CERC letters. */}
        <Reveal variant="up">
          <div className="border border-border bg-navy-deep/40 mb-16">
            <div className="grid md:grid-cols-[1.1fr_1fr]">
              <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-border">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="h-4 w-4 text-gold" aria-hidden />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-silver/70">
                    Behind the Practice
                  </span>
                </div>
                <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl leading-[1.1] mb-4">
                  Dr. Sully — Authority &amp;{" "}
                  <span className="text-gradient-gold">Coaching Background</span>
                </h3>
                <p className="text-silver text-sm md:text-base leading-relaxed mb-4">
                  FSAP is led by <strong className="text-white">Dr. Sully — PsyD · ThD · DMin · CERC</strong>,
                  a faith-integrated, trauma-informed advocate and executive
                  restoration coach based in San Antonio, Texas, with 12+
                  years of one-on-one advocacy and support experience across
                  educational, agency, medical, court-adjacent, and
                  faith-community settings.
                </p>
                <p className="text-silver text-sm md:text-base leading-relaxed mb-4">
                  The practice sits at the intersection of clinical
                  psychology, applied theology, and executive coaching — not
                  as legal counsel, and not as therapy, but as prepared,
                  strategic, documented presence in rooms where families and
                  individuals are otherwise navigating alone.
                </p>
                <p className="text-silver text-sm md:text-base leading-relaxed">
                  Every engagement is scoped in writing, boundaried by role,
                  and coordinated so nothing gets lost between systems. When
                  a case sits outside FSAP's lane, referrals are made — and
                  named — in writing.
                </p>
              </div>

              <div className="p-8 md:p-10 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="h-4 w-4 text-gold" aria-hidden />
                    <span className="font-display uppercase text-xs tracking-[0.18em] text-white/90">
                      Disciplines behind the work
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm text-silver">
                    <li className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" aria-hidden />
                      <span><strong className="text-white">PsyD</strong> — clinical psychology lens on assessment, evaluation packets, and behavioral documentation.</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" aria-hidden />
                      <span><strong className="text-white">ThD · DMin</strong> — applied theology and ministry training for faith-integrated pastoral care and ethics.</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" aria-hidden />
                      <span><strong className="text-white">CERC</strong> — Certified Executive Restoration Coach: leadership, recovery, and restoration frameworks applied to family systems.</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-gold" aria-hidden />
                    <span className="font-display uppercase text-xs tracking-[0.18em] text-white/90">
                      Systems worked inside
                    </span>
                  </div>
                  <ul className="grid grid-cols-2 gap-2 text-xs text-silver">
                    <li className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5 text-gold" aria-hidden />K–12 &amp; ARD/IEP</li>
                    <li className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5 text-gold" aria-hidden />College disability services</li>
                    <li className="flex items-center gap-2"><Stethoscope className="h-3.5 w-3.5 text-gold" aria-hidden />Medical &amp; clinical</li>
                    <li className="flex items-center gap-2"><Gavel className="h-3.5 w-3.5 text-gold" aria-hidden />Court-adjacent settings</li>
                    <li className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-gold" aria-hidden />State &amp; local agencies</li>
                    <li className="flex items-center gap-2"><Heart className="h-3.5 w-3.5 text-gold" aria-hidden />Faith communities</li>
                  </ul>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ScrollText className="h-4 w-4 text-gold" aria-hidden />
                    <span className="font-display uppercase text-xs tracking-[0.18em] text-white/90">
                      Coaching philosophy
                    </span>
                  </div>
                  <p className="text-sm text-silver leading-relaxed">
                    Prepare the person. Document the plan. Show up in the
                    room. Follow up in writing. Refer honestly when the case
                    sits outside the lane.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>



        {/* Outcome-focused composite scenarios */}
        <div className="grid md:grid-cols-3 gap-5 mb-16">
          {OUTCOME_STORIES.map(({ id, setting, challenge, action, outcome }, i) => (
            <Reveal key={id} variant="up" delay={i * 90}>
              <article className="h-full">
                <button
                  type="button"
                  onClick={() => {
                    setOpenStoryId(id);
                    trackCTA("advocacy_case_study_open", { story_id: id, setting });
                  }}
                  className="text-left w-full h-full border border-border bg-navy/60 p-6 hover:border-gold/60 hover:bg-navy/80 focus:outline-none focus:ring-2 focus:ring-gold/60 transition-colors flex flex-col group"
                  aria-label={`Open full case study: ${setting}`}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="h-4 w-4 text-gold" aria-hidden />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-silver/70">
                      {setting}
                    </span>
                  </div>
                  <dl className="space-y-3 text-sm leading-relaxed">
                    <div>
                      <dt className="font-display uppercase text-xs tracking-[0.18em] text-white/80">
                        Challenge
                      </dt>
                      <dd className="text-silver mt-1">{challenge}</dd>
                    </div>
                    <div>
                      <dt className="font-display uppercase text-xs tracking-[0.18em] text-white/80">
                        FSAP Role
                      </dt>
                      <dd className="text-silver mt-1">{action}</dd>
                    </div>
                    <div>
                      <dt className="font-display uppercase text-xs tracking-[0.18em] text-gold">
                        Outcome
                      </dt>
                      <dd className="text-white/90 mt-1">{outcome}</dd>
                    </div>
                  </dl>
                  <span className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-gold group-hover:gap-2 transition-all">
                    Read full case study <ArrowRight className="h-3 w-3" />
                  </span>
                  <p className="mt-auto pt-5 text-[10px] uppercase tracking-[0.2em] text-silver/50">
                    Illustrative composite · Outcomes vary
                  </p>
                </button>
              </article>
            </Reveal>
          ))}
        </div>


        {/* Client voices — real quotes only when release is on file */}
        <Reveal variant="up">
          <div className="border-t border-border pt-12">
            <div className="flex items-center justify-center gap-2 mb-8">
              <Quote className="h-4 w-4 text-gold" aria-hidden />
              <span className="eyebrow !mx-0">Client Voices</span>
            </div>

            {CLIENT_VOICES.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                {CLIENT_VOICES.map(({ quote, attribution, setting }) => (
                  <figure
                    key={attribution + setting}
                    className="border border-border bg-navy/60 p-6"
                  >
                    <blockquote className="text-white/90 text-base leading-relaxed">
                      <span className="text-gold">"</span>
                      {quote}
                      <span className="text-gold">"</span>
                    </blockquote>
                    <figcaption className="mt-4 text-xs uppercase tracking-[0.2em] text-silver/70">
                      {attribution} · {setting}
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="max-w-2xl mx-auto text-center border border-dashed border-border p-8">
                <p className="text-silver text-sm leading-relaxed">
                  Written testimonials are published here only after a signed
                  release is on file. If you've worked with FSAP and would like
                  to share your story, reach out through the intake form and
                  we'll send a release for your review.
                </p>
                <Link
                  to="/share-your-story?lane=advocacy"
                  className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.22em] text-gold hover:text-gold-bright"
                >
                  Share your story
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>

    {/* DIFFERENTIATION */}
    <section className="py-20 md:py-28 border-t border-border bg-navy-deep/40">
      <div className="container">
        <Reveal variant="up" className="max-w-3xl mb-14 text-center mx-auto">
          <span className="eyebrow !mx-auto inline-flex">Positioning</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            "This is not basic consulting. This is{" "}
            <span className="text-gradient-gold">
              strategic advocacy, real-world presence,
            </span>{" "}
            and support when families need someone experienced in the room."
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {DIFFERENTIATORS.map(({ icon: Icon, title, desc }, i) => (
            <Reveal key={title} variant="up" delay={i * 70}>
              <div className="h-full border border-border bg-navy/60 p-6 hover:border-gold/40 transition-colors">
                <Icon className="h-6 w-6 text-gold" />
                <h3 className="font-display uppercase text-white text-base mt-4 leading-tight">
                  {title}
                </h3>
                <p className="text-silver text-sm mt-3 leading-relaxed">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* LEGAL & ETHICAL BOUNDARIES */}
    <section className="py-20 md:py-28 border-t border-border">
      <div className="container max-w-4xl">
        <Reveal variant="up">
          <span className="eyebrow">Legal & Ethical Boundaries</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Clear scope.{" "}
            <span className="text-gradient-gold">Documented boundaries.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            FSAP is an advocacy and support service. It is <strong className="text-white">not</strong> a law
            firm, legal practice, medical provider, mental health emergency
            service, transportation company, or licensed emergency response
            agency.
          </p>
        </Reveal>

        <Reveal variant="up" delay={80}>
          <ul className="mt-10 space-y-3">
            {BOUNDARIES.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 border border-border bg-navy/60 p-5"
              >
                <ScrollText className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm md:text-base leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>

        <p className="text-silver/60 text-xs mt-8 leading-relaxed">
          Full scope of representation, communication standards, confidentiality
          practices, and release-of-information procedures are documented in
          writing before service activation.
        </p>
      </div>
    </section>

    {/*
      DISCOVERY BOOKING
      -----------------
      Inline Calendly widget so qualified visitors can grab a discovery slot
      without leaving the page. The URL is env-configurable
      (VITE_FSAP_DISCOVERY_URL) so it can be rotated without a code change.
      If the env var isn't set yet, the widget renders a friendly fallback
      pointing to /strategy-session — never a broken embed.
    */}
    <section id="book" className="py-20 md:py-28 border-t border-border bg-navy-deep/40">
      <div className="container max-w-5xl">
        <Reveal variant="up" className="max-w-3xl mx-auto text-center mb-10">
          <span className="eyebrow !mx-auto inline-flex">Consultation Intake</span>
          <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
            Share your context. <br className="hidden md:block" />
            <span className="text-gradient-gold">Book the discovery call.</span>
          </h2>
          <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
            Tell us about the student, the setting, and the tier you're
            considering. Submit and you're routed to the paid discovery
            scheduler so we can confirm fit before any engagement is signed.
          </p>
        </Reveal>

        <Reveal variant="up" delay={60}>
          <FSAPConsultForm />
        </Reveal>

        <Reveal variant="up" delay={140} className="mt-14">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <span className="eyebrow !mx-auto inline-flex">Or pick a time directly</span>
            <p className="text-silver text-sm mt-4 leading-relaxed">
              Prefer to schedule first and share context on the call? Use the
              discovery scheduler below.
            </p>
          </div>
          <CalendlyInlineWidget
            url={import.meta.env.VITE_FSAP_DISCOVERY_URL as string | undefined}
            utm={{
              utmSource: "drsullyspeaks.com",
              utmMedium: "advocacy-page",
              utmCampaign: "fsap-discovery",
            }}
            minHeight={780}
            fallbackHref={buildInquireHref({ lane: "advocacy", source: "calendly_fallback" })}
          />
        </Reveal>
      </div>
    </section>

    <FAQSection
      items={FSAP_FAQ}
      eyebrow="FSAP · Frequently Asked"
      heading="Straight answers about FSAP."
      subheading="The questions families, schools, and referring partners ask most often before starting the intake funnel."
    />


    <ConversionBand
      eyebrow="Limited Availability"
      heading="Book a consultation to determine whether ongoing FSAP support or a focused à la carte service is the right fit."
      subheading="FSAP is intentionally capped so every client gets true partnership. Consultations begin with a short screening call, followed by a paid discovery."
      primaryLabel={PRIMARY_CTAS.advocacy.label}
      primaryHref={PRIMARY_CTAS.advocacy.href}
      onPrimaryClick={() =>
        trackPlacementCTA("final", PRIMARY_CTAS.advocacy, { surface: "advocacy_conversion_band" })
      }
      secondaryLabel="See FSAP Plans"
      secondaryHref="#retainer"
    />

    <StrategySessionModal
      open={!!strategyTier}
      onOpenChange={(o) => !o && setStrategyTier(null)}
      tierLabel={strategyTier ?? ""}
      tierId={strategyTier?.toLowerCase().replace(/\s+/g, "-")}
      source="advocacy_retainer_cards"
    />

    <SiteFooter />

    {/* Sticky mobile CTA — hidden on md+ where inline CTAs stay reachable */}
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gold/40 bg-navy-deep/95 backdrop-blur supports-[backdrop-filter]:bg-navy-deep/80 pb-[env(safe-area-inset-bottom)]"
      role="region"
      aria-label="Book a discovery call"
      data-book-location="sticky_mobile"
    >
      <div className="flex items-stretch gap-2 p-3">
        <a
          href="#book"
          onClick={() =>
            trackPlacementCTA("sticky", PRIMARY_CTAS.advocacy, { surface: "advocacy_sticky_mobile" }) &&
            trackCTA("advocacy_book_discovery_click", {
              location: "sticky_mobile",
              destination: "#book",
            })
          }
          className="flex-1 inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-[11px] px-4 py-3.5 shadow-cta"
        >
          {PRIMARY_CTAS.advocacy.label}
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
        <a
          href="#compare"
          onClick={() =>
            trackCTA("advocacy_see_plans_click", {
              location: "sticky_mobile",
              destination: "#compare",
            })
          }
          className="inline-flex items-center justify-center border border-gold/50 text-white font-display uppercase tracking-[0.18em] text-[11px] px-4 py-3.5 hover:bg-gold/10"
          aria-label="See FSAP plans"
        >
          Plans
        </a>
      </div>
    </div>

    {/* Case study modal */}
    <Dialog open={openStoryId !== null} onOpenChange={(open) => !open && setOpenStoryId(null)}>
      <DialogContent className="max-w-2xl bg-navy border-border text-foreground max-h-[85vh] overflow-y-auto">
        {openStory && (
          <>
            <DialogHeader>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">
                {openStory.setting}
              </div>
              <DialogTitle className="font-display text-2xl text-white">
                Case Study — Anonymized Composite
              </DialogTitle>
              <DialogDescription className="text-silver/80 pt-1">
                Timeline: {openStory.timeline}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 text-sm leading-relaxed mt-4">
              <section>
                <h3 className="font-display uppercase text-xs tracking-[0.18em] text-white/80 mb-1">Context</h3>
                <p className="text-silver">{openStory.context}</p>
              </section>
              <section>
                <h3 className="font-display uppercase text-xs tracking-[0.18em] text-white/80 mb-1">Challenge</h3>
                <p className="text-silver">{openStory.challenge}</p>
              </section>
              <section>
                <h3 className="font-display uppercase text-xs tracking-[0.18em] text-white/80 mb-2">Process Steps</h3>
                <ol className="space-y-2 list-decimal list-inside text-silver marker:text-gold">
                  {openStory.process.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </section>
              <section>
                <h3 className="font-display uppercase text-xs tracking-[0.18em] text-gold mb-2">Results (Anonymized)</h3>
                <ul className="space-y-2 text-white/90">
                  {openStory.results.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" aria-hidden />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </section>
              <p className="text-[10px] uppercase tracking-[0.2em] text-silver/50 border-t border-border pt-4">
                Illustrative composite drawn from 12+ years of practice · Outcomes vary · No guarantees
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/advocacy/intake"
                  onClick={() => trackCTA("advocacy_case_study_intake", { story_id: openStory.id })}
                  className="inline-flex items-center gap-2 bg-gold text-navy font-display uppercase tracking-[0.18em] text-[11px] px-4 py-3 hover:brightness-110"
                >
                  Start intake <ArrowRight className="h-3 w-3" />
                </Link>
                <Link
                  to="/advocacy/pricing"
                  onClick={() => trackCTA("advocacy_case_study_pricing", { story_id: openStory.id })}
                  className="inline-flex items-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.18em] text-[11px] px-4 py-3 hover:bg-gold/10"
                >
                  See pricing
                </Link>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  </div>
  );
};



export default Advocacy;
